import "lib/@hooks/useEvent"
import { useMemo, useState } from "react"
import { Box, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorDraftTokens } from "./hooks/use-schedule-editor-draft-tokens"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { getMacros } from "authoring/routes/macro/controller/get-macros"
import { buildMacroLookup, buildMacroNameLookup } from "authoring/routes/macro/lib/macro-text-utils"
import { resolveMacrosByIds } from "authoring/routes/macro/lib/macro-guidance-utils"
import { MacroGuidanceList } from "authoring/routes/macro/components/macro-guidance-list"
import { MacroGuidanceDetail } from "authoring/routes/macro/components/macro-guidance-detail"
import { MacroGuidanceOpen } from "authoring/routes/macro/macro-events"
import { ComponentsInUseList } from "./components-in-use-list"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { FieldUpdate } from "event-definitions"
import { useScheduleEditorIds } from "./hooks/use-schedule-editor-ids"
import { AmendsChanged, DraftTokensChanged } from "./schedule-editor-event-definitions.mjs"

const VIEW_COMPONENTS = "components"
const VIEW_MACROS = "macros"
const LIST_MAX_HEIGHT = 500

export function AuthoringMacroGuidance() {
    const [view, setView] = useState(VIEW_COMPONENTS)
    const [selectedMacroId, setSelectedMacroId] = useState(null)
    const { target, rawSchedule } = useBoundContext()
    const { draftId } = useScheduleEditorIds()
    const { draftTokenIdSet } = useScheduleEditorDraftTokens()
    const refresh = useRefresh(noChange)
    FieldUpdate("content").useEvent(refresh)
    FieldUpdate("note").useEvent(refresh)
    DraftTokensChanged(draftId).useEvent(refresh)
    AmendsChanged(draftId).useEvent(refresh)
    const macroIdsInUse = useMemo(() => [...draftTokenIdSet] ?? [], [draftTokenIdSet])
    const scheduleTasks = (rawSchedule ?? target)?.tasks
    const componentTokenIds = useMemo(resolveComponentTokenIds, [macroIdsInUse])
    const regularMacroIds = useMemo(resolveRegularMacroIds, [macroIdsInUse])
    const { data: macros } = getMacros.useResults.status({ retrieveDeleted: false })
    const allMacrosById = useMemo(buildLookupById, [macros])
    const allMacrosByName = useMemo(buildLookupByName, [macros])
    const macrosInUse = useMemo(resolveMacrosInUse, [regularMacroIds, allMacrosById, allMacrosByName])
    const selectedMacro = selectedMacroId ? allMacrosById[selectedMacroId] ?? null : null
    MacroGuidanceOpen.useEvent(handleMacroGuidanceOpen)

    return (
        <Stack spacing={1} sx={{ px: 1.25, py: 1.25 }}>
            <ToggleButtonGroup value={view} exclusive size="small" onChange={handleViewChange} fullWidth>
                <ToggleButton value={VIEW_COMPONENTS}>Components In Use</ToggleButton>
                <ToggleButton data-cy="schedule-macros-in-use" value={VIEW_MACROS}>
                    Macros In Use
                </ToggleButton>
            </ToggleButtonGroup>
            <If truthy={view === VIEW_COMPONENTS}>
                <ComponentsInUseList componentTokenIds={componentTokenIds} scheduleTasks={scheduleTasks} />
                <Else>
                    <Box
                        sx={{
                            overflow: "hidden",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            px: 1,
                            py: 1,
                        }}
                    >
                        <If truthy={!selectedMacro}>
                            <MacroGuidanceList
                                macros={macrosInUse}
                                selectedId={selectedMacroId}
                                onOpen={setSelectedMacroId}
                                emptyMessage="No macros in use."
                                maxHeight={LIST_MAX_HEIGHT}
                            />
                            <Else>
                                <MacroGuidanceDetail
                                    selectedMacro={selectedMacro}
                                    onBack={handleClearSelection}
                                    maxHeight={LIST_MAX_HEIGHT}
                                />
                            </Else>
                        </If>
                    </Box>
                </Else>
            </If>
        </Stack>
    )

    function handleViewChange(_event, value) {
        if (!value) return
        setView(value)
        setSelectedMacroId(null)
    }

    function handleClearSelection() {
        setSelectedMacroId(null)
    }

    function handleMacroGuidanceOpen(macroId) {
        if (!macroId) return
        setView(VIEW_MACROS)
        setSelectedMacroId(macroId)
    }

    function resolveComponentTokenIds() {
        const seen = new Set()
        return macroIdsInUse.filter(filterUniqueComponentTokens)

        function filterUniqueComponentTokens(id) {
            if (!id?.startsWith("cmp:")) return false
            const taskId = extractTaskIdFromToken(id)
            if (seen.has(taskId)) return false
            seen.add(taskId)
            return true
        }
    }

    function resolveRegularMacroIds() {
        return macroIdsInUse.filter(isRegularMacro)
    }

    function buildLookupById() {
        return buildMacroLookup(macros ?? [])
    }

    function buildLookupByName() {
        return buildMacroNameLookup(macros ?? [])
    }

    function resolveMacrosInUse() {
        return resolveMacrosByIds(regularMacroIds, allMacrosById, allMacrosByName).sort(compareByName)
    }
}

function isRegularMacro(id) {
    return !!id && !id.startsWith("cmp:")
}

function extractTaskIdFromToken(tokenId) {
    const rest = tokenId.slice(4)
    const separatorIndex = rest.indexOf("::")
    if (separatorIndex === -1) return rest
    return rest.slice(0, separatorIndex)
}

function compareByName(a, b) {
    const nameA = a?.name ?? a?.id ?? ""
    const nameB = b?.name ?? b?.id ?? ""
    return nameA.localeCompare(nameB, undefined, { sensitivity: "base" })
}
